import type { RealizationCandidateDto, TaskDto } from '@bqcrm/shared';
import { Alert, Badge, Button, Checkbox, Group, Modal, Radio, Stack, Text } from '@mantine/core';
import { IconLock, IconUsersGroup } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { CompanyBadgeById, TradeBadge } from '../../components/Badges';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { notifyError } from '../../notify';
import { useTeams } from './common';
import { PendingAnnexAlert } from './PendingAnnexAlert';

/**
 * „Zaplanuj realizację”: wybór branż sprawy (po umowie) — jedno wspólne zadanie dla jednej ekipy
 * (czynności wszystkich branż pogrupowane, wspólny protokół odbioru) albo osobne zadania dla różnych ekip.
 * Podpowiada ekipy, które mają zaznaczone wybrane branże. Po utworzeniu — planowanie terminu i ekipy.
 */
export function RealizationDialog({ caseId, preselect, onDone, onClose }: { caseId: string; preselect?: string[]; onDone: (tasks: TaskDto[]) => void; onClose: () => void }) {
  const q = useQuery({ queryKey: ['realization', caseId], queryFn: () => api<RealizationCandidateDto[]>(`/cases/${caseId}/realization`) });
  const teams = useTeams();
  const free = (q.data ?? []).filter((c) => !c.task && c.blockedBy === null);
  const [picked, setPicked] = useState<string[]>([]);
  const [mode, setMode] = useState<'wspolne' | 'osobne'>('wspolne');
  useEffect(() => {
    // z zadania branży — tylko ta branża; bez wskazania — wszystkie branże osoby jeszcze bez zadania
    if (q.data) setPicked(preselect?.length ? preselect.filter((id) => free.some((c) => c.scopeId === id)) : free.map((c) => c.scopeId));
    // tylko po wczytaniu listy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);
  const create = useMutation({
    mutationFn: () => api<TaskDto[]>(`/cases/${caseId}/realization`, { method: 'POST', body: { scopeIds: picked, separate: picked.length > 1 && mode === 'osobne' } }),
    onSuccess: onDone,
    onError: notifyError,
  });
  const chosen = free.filter((c) => picked.includes(c.scopeId));
  // ekipy, które mają zaznaczone wszystkie wybrane branże (wspólne zadanie) albo którąkolwiek (osobne)
  const fitting = (teams.data ?? []).filter(
    (t) => t.active && chosen.length > 0 && (mode === 'osobne' && chosen.length > 1 ? chosen.some((c) => t.tradeCodes.includes(c.tradeCode)) : chosen.every((c) => t.tradeCodes.includes(c.tradeCode))),
  );
  const toggle = (id: string, on: boolean) => setPicked((p) => (on ? [...p, id] : p.filter((x) => x !== id)));

  return (
    <Modal opened onClose={onClose} title="Planowanie realizacji" size="lg">
      <QueryState isLoading={q.isLoading} error={q.error}>
        <Stack>
          {!q.data?.length && <EmptyState compact title="Brak branż z umową" hint="Realizację planuje się po wydaniu umowy." />}
          {!!q.data?.length && (
            <Text size="sm" c="dimmed">
              Wybierz branże. Branża decyduje o czynnościach ekipy i elementach protokołu odbioru.
            </Text>
          )}
          <Stack gap="xs">
            {(q.data ?? []).map((c) => (
              <Checkbox
                key={c.scopeId}
                disabled={!!c.task || c.blockedBy !== null}
                checked={!c.task && c.blockedBy === null && picked.includes(c.scopeId)}
                onChange={(e) => toggle(c.scopeId, e.currentTarget.checked)}
                label={
                  <Group gap={6} wrap="wrap">
                    <TradeBadge code={c.tradeCode} />
                    <Text size="sm" fw={500}>
                      {c.tradeName}
                    </Text>
                    <CompanyBadgeById id={c.companyId} />
                    {c.contractNumber && (
                      <Text size="xs" c="dimmed">
                        {c.contractNumber}
                      </Text>
                    )}
                    {c.task && (
                      <Badge size="xs" variant="light" color="gray">
                        już jest zadanie {c.task.number}
                      </Badge>
                    )}
                    {c.annexNumber != null && (
                      <Badge size="xs" variant="light" color="blue">
                        prace z aneksu {c.annexNumber}
                      </Badge>
                    )}
                    {!c.task && !c.conditionsMet && (
                      <Badge size="xs" variant="light" color="orange" leftSection={<IconLock size={10} />}>
                        warunki startu niespełnione
                      </Badge>
                    )}
                  </Group>
                }
                description={
                  c.blockedBy !== null && !c.task
                    ? c.blockedBy
                      ? `realizację planuje ${c.blockedBy} (przypisane zadanie)`
                      : 'nie obsługujesz tej branży (brak funkcji koordynatora w branży ani przypisanego zadania)'
                    : !c.task && !c.conditionsMet
                      ? `${c.conditionsText} — można zaplanować warunkowo, ekipa nie rozpocznie przed spełnieniem.`
                      : undefined
                }
              />
            ))}
          </Stack>
          <PendingAnnexAlert caseId={caseId} scopeIds={picked} />
          {picked.length > 1 && (
            <Radio.Group value={mode} onChange={(v) => setMode(v as 'wspolne' | 'osobne')} label="Podział na zadania">
              <Stack gap="xs" mt={6}>
                <Radio value="wspolne" label="Jedno wspólne zadanie" description="Jedna ekipa: czynności wszystkich branż w jednym zadaniu (pogrupowane według branż), wspólny protokół odbioru." />
                <Radio value="osobne" label="Osobne zadanie dla każdej branży" description="Każda branża osobno — możesz je przydzielić różnym ekipom i terminom." />
              </Stack>
            </Radio.Group>
          )}
          {chosen.length > 0 && (
            <Alert variant="light" color={fitting.length ? 'eko' : 'yellow'} icon={<IconUsersGroup size={18} />} p="xs">
              <Text size="sm">
                {fitting.length
                  ? `Ekipy z ${mode === 'osobne' && chosen.length > 1 ? 'tymi branżami' : chosen.length > 1 ? 'wszystkimi wybranymi branżami' : 'tą branżą'}: ${fitting.map((t) => t.name).join(', ')}`
                  : 'Żadna ekipa nie ma zaznaczonych wybranych branż (Ekipy → Branże) — wybierzesz ekipę ręcznie.'}
              </Text>
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Anuluj
            </Button>
            <Button disabled={!picked.length} loading={create.isPending} onClick={() => create.mutate()}>
              {picked.length > 1 && mode === 'osobne' ? `Utwórz ${picked.length} zadania i zaplanuj` : 'Utwórz i zaplanuj'}
            </Button>
          </Group>
        </Stack>
      </QueryState>
    </Modal>
  );
}
